import React from "react";
import styled from "styled-components";

import { Button } from "@material-ui/core";
import { KeyboardArrowLeft as KeyboardArrowLeftIcon, Info as InfoIcon } from "@material-ui/icons";

import { AiProvider } from "common/aiConstants";
import { ActionPermissionsObject, PermissionActionType } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { openPricingPage } from "js/core/utilities/externalLinks";
import { ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { FlexSpacer, Gap20 } from "js/react/components/Gap";
import { Notice } from "js/react/components/Notice";
import { BlueButton, UIPane } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";

const Container = styled.div`
    width: 100%;
    height: 100%;
    padding: 0px;
    background: ${themeColors.lightGray};
    display: flex;
    flex-direction: column;
    font-family: 'Source Sans Pro';
    font-style: normal;
`;

const ContentContainer = styled.div`
    width: 100%;
    height: 100%;
    padding: 20px 20px 0px 20px;
    overflow: auto;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;

const FooterContainer = styled.div`
    width: 100%;
    padding: 20px;
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const FooterCreditsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;

    > span {
        color: ${themeColors.darkGray};
        font-size: 12px;
        font-family: Source Sans Pro;
        font-weight: 600;
        line-height: 150%;
    }
`;

export const PromptSectionContainer = styled.div`
    padding: 30px;
    background: white;
    width: 100%;
`;

const CreditsInfoButton = styled(InfoIcon)`
    &&& {
        color: ${themeColors.ui_blue};
        height: 18px;
        width: 18px;
        cursor: pointer;
    }
`;

const DialogMessageContainer = styled.div`
    > p:first-child {
        font-weight: 400;
        font-size: 16px;
        line-height: 125%;
        letter-spacing: 0.5px;
        color: #222222;
    }

    > p {
        font-weight: 600;
        font-size: 18px;
        line-height: 120%;
        letter-spacing: 0.5px;
    }
`;

const BackButtonContainer = styled(Button)`
    &&& {
        color: ${themeColors.mediumGray};

        svg {
            width: 30px;
            height: 30px;
        }
    }
`;

function BackButton({ onClick, disabled }: { onClick: React.MouseEventHandler<HTMLButtonElement>, disabled: boolean }) {
    return (<BackButtonContainer
        onClick={onClick}
        disabled={disabled}
    >
        <KeyboardArrowLeftIcon /> Back
    </BackButtonContainer>);
}

interface PaneFooterProps {
    aiCreditsBalance?: number;
    showBackButton?: boolean;
    backButtonDisabled?: boolean;
    provider?: AiProvider;
    onBackButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
    actionButtonText?: string;
    onActionButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
    actionButtonDisabled?: boolean;
}

export function PaneFooter({
    aiCreditsBalance = undefined,
    showBackButton = false,
    backButtonDisabled = false,
    onBackButtonClick = () => { },
    actionButtonText,
    onActionButtonClick,
    actionButtonDisabled,
}: PaneFooterProps) {
    const handleShowGetCreditsDialog = () => {
        const workspaceId = AppController.workspaceId;

        ShowConfirmationDialog({
            title: "Generate more slides and presentations with more credits.",
            message: <DialogMessageContainer>
                <p>Use credits to create images based on your prompts. To purchase additional credits for you and your
                    team, contact us.</p>
                <p>Your workspace has {aiCreditsBalance} credits remaining.</p>
            </DialogMessageContainer>,
            okButtonLabel: "Contact Us",
            cancelButtonLabel: "Never mind",
            acceptCallback: () => window.open("mailto:support@beautiful.ai")
        });
    };

    return (<FooterContainer>
        {/* Left side */}
        {showBackButton && <BackButton disabled={backButtonDisabled} onClick={onBackButtonClick} />}

        {/* @ts-ignore */}
        <FlexSpacer />

        {/* Right side */}
        {aiCreditsBalance !== undefined && <FooterCreditsContainer>
            {aiCreditsBalance !== -1 && <>
                <span>{aiCreditsBalance} Credits Available</span>
                <CreditsInfoButton onClick={handleShowGetCreditsDialog} />
            </>}
        </FooterCreditsContainer>}
        <Gap20 />
        {actionButtonText && <BlueButton disabled={actionButtonDisabled} onClick={onActionButtonClick}>{actionButtonText}</BlueButton>}
    </FooterContainer>);
}

interface PaneContainerProps {
    cta: string;
    children: React.ReactNode;
    fetching?: boolean;
}

export function PaneContainer({
    children,
    cta,
    fetching = false
}: PaneContainerProps) {
    let Children = children;

    if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, PermissionActionType.USE)) {
        Children = (
            <Notice
                title="Let DesignerBot generate custom slides and presentations."
                message={<>
                    <div>Upgrade to Beautiful.ai Pro to use AI to generate content for you.</div>
                    <BlueButton
                        // @ts-ignore
                        style={{ marginBottom: 0 }}
                        onClick={() => openPricingPage({ cta })}
                    >
                        Get Started
                    </BlueButton>
                </>}
            />
        );
    }

    return (<UIPane>
        <Container>
            <FetchingClickShield visible={fetching} backgroundColor={themeColors.lightGray} />
            {Children}
        </Container>
    </UIPane>);
}

export const PaneContent = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
    function PaneContent({ children }: { children: React.ReactNode }, ref: React.Ref<HTMLDivElement>) {
        return (<ContentContainer ref={ref}>
            {children}
        </ContentContainer>);
    }
);

export const InputContainer = styled.div<{ height?: number }>`
    width: 100%;
    height: ${props => props.height ? `${props.height}px` : "80px"};
    padding: 10px;

    display: flex;
    flex-flow: row;
    align-items: flex-start;

    border: 1px solid ${themeColors.lightGray};
    background: white;

    :focus-within {
        border: 1px solid ${themeColors.ui_blue};
    }

    div.MuiFormControl-root {
        width: 100%;
        height: 100%;
        max-height: ${props => props.height ? `${props.height}px` : "80px"};
        overflow: auto;
    }

    textarea.MuiInputBase-input::webkit-input-placeholder {
        font-style: italic;
        font-weight: 400;
        font-size: 16px;
        line-height: 150%;
        color: ${themeColors.darkGray};
    }

    textarea.MuiInputBase-input::disabled {
        color: ${themeColors.darkGray};
        user-select: none;
    }
`;

export const ImageOptionsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    width: 100%;
    padding: 30px;
    gap: 20px;
`;

export const PopupContainerStyles = {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    border: "1px solid #EEE",
    height: 38,
};
